import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import "./PageTemplate.css";
import styles from "./Achievement.module.css";

type Category = "academic" | "sports" | "community" | "all";

const Achievement: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    {
      id: "academic" as Category,
      name: t("achievement.categories.academic"),
      icon: "/images/achievements/icon-academic.png",
    },
    {
      id: "sports" as Category,
      name: t("achievement.categories.sports"),
      icon: "/images/achievements/icon-sports.png",
    },
    {
      id: "community" as Category,
      name: t("achievement.categories.community"),
      icon: "/images/achievements/icon-community.png",
    },
  ];

  const academicCertificates = [
    {
      id: 10,
      image: "/images/achievements/academic-cert-10.jpg",
      title: t("achievement.academic.cert10.title"),
      description: t("achievement.academic.cert10.description"),
    },
    {
      id: 1,
      image: "/images/achievements/academic-cert-1.png",
      title: t("achievement.academic.cert1.title"),
      description: t("achievement.academic.cert1.description"),
    },
    {
      id: 2,
      image: "/images/achievements/academic-cert-2.png",
      title: t("achievement.academic.cert2.title"),
      description: t("achievement.academic.cert2.description"),
    },
    {
      id: 3,
      image: "/images/achievements/academic-cert-3.png",
      title: t("achievement.academic.cert3.title"),
      description: t("achievement.academic.cert3.description"),
    },
    {
      id: 4,
      image: "/images/achievements/academic-cert-4.png",
      title: t("achievement.academic.cert4.title"),
      description: t("achievement.academic.cert4.description"),
    },
    {
      id: 5,
      image: "/images/achievements/academic-cert-5.png",
      title: t("achievement.academic.cert5.title"),
      description: t("achievement.academic.cert5.description"),
    },
    {
      id: 6,
      image: "/images/achievements/academic-cert-6.png",
      title: t("achievement.academic.cert6.title"),
      description: t("achievement.academic.cert6.description"),
    },
    {
      id: 7,
      image: "/images/achievements/academic-cert-7.png",
      title: t("achievement.academic.cert7.title"),
      description: t("achievement.academic.cert7.description"),
    },
    {
      id: 8,
      image: "/images/achievements/academic-cert-8.png",
      title: t("achievement.academic.cert8.title"),
      description: t("achievement.academic.cert8.description"),
    },
    {
      id: 9,
      image: "/images/achievements/academic-cert-9.png",
      title: t("achievement.academic.cert9.title"),
      description: t("achievement.academic.cert9.description"),
    },
  ];

  const sportsCertificates = [
    {
      id: 1,
      image: "/images/achievements/sports-cert-1.png",
      title: t("achievement.sports.cert1.title"),
      description: t("achievement.sports.cert1.description"),
    },
    {
      id: 2,
      image: "/images/achievements/sports-cert-2.png",
      title: t("achievement.sports.cert2.title"),
      description: t("achievement.sports.cert2.description"),
    },
    {
      id: 3,
      image: "/images/achievements/sports-cert-3.png",
      title: t("achievement.sports.cert3.title"),
      description: t("achievement.sports.cert3.description"),
    },
    {
      id: 4,
      image: "/images/achievements/sports-cert-4.jpg",
      title: t("achievement.sports.cert4.title"),
      description: t("achievement.sports.cert4.description"),
    },
    {
      id: 5,
      image: "/images/achievements/sports-cert-5.png",
      title: t("achievement.sports.cert5.title"),
      description: t("achievement.sports.cert5.description"),
    },
    {
      id: 6,
      image: "/images/achievements/sports-cert-6.png",
      title: t("achievement.sports.cert6.title"),
      description: t("achievement.sports.cert6.description"),
    },
  ];

  const communityCertificates = [
    {
      id: 1,
      image: "/images/achievements/community-cert-1.jpg",
      title: t("achievement.community.cert1.title"),
      description: t("achievement.community.cert1.description"),
    },
    {
      id: 2,
      image: "/images/achievements/community-cert-2.png",
      title: t("achievement.community.cert2.title"),
      description: t("achievement.community.cert2.description"),
    },
    {
      id: 3,
      image: "/images/achievements/community-cert-3.png",
      title: t("achievement.community.cert3.title"),
      description: t("achievement.community.cert3.description"),
    },
    {
      id: 4,
      image: "/images/achievements/community-cert-4.png",
      title: t("achievement.community.cert4.title"),
      description: t("achievement.community.cert4.description"),
    },
    {
      id: 5,
      image: "/images/achievements/community-cert-5.png",
      title: t("achievement.community.cert5.title"),
      description: t("achievement.community.cert5.description"),
    },
    {
      id: 6,
      image: "/images/achievements/community-cert-6.png",
      title: t("achievement.community.cert6.title"),
      description: t("achievement.community.cert6.description"),
    },
    {
      id: 7,
      image: "/images/achievements/community-cert-7.jpg",
      title: t("achievement.community.cert7.title"),
      description: t("achievement.community.cert7.description"),
    },
  ];

  const shouldShowCategory = (category: Category) => {
    return selectedCategory === "all" || selectedCategory === category;
  };

  return (
    <div className={styles.achievementPage}>
      <Header />
      <main className={styles.achievementContent}>
        <div className={styles.achievementContainer}>
          <div className={styles.achievementHeader}>
            <h1 className={styles.pageTitle}>{t("achievement.title")}</h1>
            <p className={styles.pageSubtitle}>
              {t("achievement.subtitle")}
            </p>
          </div>

          <div className={styles.categoryButtons}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryButton} ${
                  selectedCategory === category.id ? styles.active : ""
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                <img src={category.icon} alt={category.name} />
                <span>{category.name}</span>
              </button>
            ))}
          </div>

          {shouldShowCategory("academic") && (
            <div className={styles.certificatesSection}>
              <h2 className={styles.sectionTitle}>
                {t("achievement.sectionTitles.academic")}
              </h2>
              <div className={styles.certificatesGrid}>
                {academicCertificates.map((cert) => (
                  <div key={cert.id} className={styles.certificateCard}>
                    <div className={styles.certificateImage}>
                      <img src={cert.image} alt={cert.title} />
                    </div>
                    <div className={styles.certificateInfo}>
                      <h3 className={styles.certificateTitle}>{cert.title}</h3>
                      <p className={styles.certificateDescription} dangerouslySetInnerHTML={{ __html: cert.description }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {shouldShowCategory("sports") && (
            <div className={styles.certificatesSection}>
              <h2 className={styles.sectionTitle}>{t("achievement.sectionTitles.sports")}</h2>
              <div className={styles.certificatesGrid}>
                {sportsCertificates.map((cert) => (
                  <div key={cert.id} className={styles.certificateCard}>
                    <div className={styles.certificateImage}>
                      <img src={cert.image} alt={cert.title} />
                    </div>
                    <div className={styles.certificateInfo}>
                      <h3 className={styles.certificateTitle}>{cert.title}</h3>
                      <p className={styles.certificateDescription} dangerouslySetInnerHTML={{ __html: cert.description }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {shouldShowCategory("community") && (
            <div className={styles.certificatesSection}>
              <h2 className={styles.sectionTitle}>
                {t("achievement.sectionTitles.community")}
              </h2>
              <div className={styles.certificatesGrid}>
                {communityCertificates.map((cert) => (
                  <div key={cert.id} className={styles.certificateCard}>
                    <div className={styles.certificateImage}>
                      <img src={cert.image} alt={cert.title} />
                    </div>
                    <div className={styles.certificateInfo}>
                      <h3 className={styles.certificateTitle}>{cert.title}</h3>
                      <p className={styles.certificateDescription} dangerouslySetInnerHTML={{ __html: cert.description }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Achievement;
